import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
} from "antd";
import { Store } from "antd/es/form/interface";
import { v4 as uuidv4 } from "uuid";
import TextArea from "antd/es/input/TextArea";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import StoreService from "../services/stores.service";
import ServiceService from "../services/services.service";
import toast from "react-hot-toast";
import { useStore } from "../context/StoreContext";

const CreateService = () => {
  const navigate = useNavigate();
  const { stores } = useStore();
  const [form] = Form.useForm();
  const storeServices = Form.useWatch("storeServices", form);
  const { id } = useParams();
  const isUpdate = !!id;
  const ctcButtonTxt = isUpdate ? "Update" : "Create";
  const newFieldsIds = useRef<string[]>([]);

  useEffect(() => {
    if (id) {
      fetchService();
    }
    form.setFieldValue("storeServices", [
      {
        id: uuidv4(),
        storeId: "",
        price: undefined,
        memberPrice: undefined,
        available: true,
      },
    ]);
  }, []);

  const fetchService = async () => {
    if (id) {
      const res = await ServiceService.Get(id);
      if (res.success) {
        const service = res.data;
        form.setFieldsValue({
          name: service.name,
          description: service.description,
          gender: service.gender,
          duration: service.duration,
          storeServices: service.storeServices.map((item) => ({
            id: item.id,
            storeId: item.storeId,
            price: item.price,
            available: item.available,
            memberPrice: item.memberPrice,
          })),
        });
      }
    }
  };

  const handleSubmit = async (value: any) => {
    const res = isUpdate
      ? await ServiceService.update(id, value)
      : await ServiceService.create(value);
    if (res?.success) {
      toast.success("Service created successfully");
      navigate("/services");
    }
  };

  const removeStoreService = async (storeServiceId: string) => {
    if (id) {
      const res = await ServiceService.deleteStoreService(id, storeServiceId);
      if (res?.success) {
        form.setFieldValue(
          "storeServices",
          storeServices.filter((item: any) => item.id !== storeServiceId)
        );
      }
    }
  };

  return (
    <div>
      <Breadcrumb
        items={[
          {
            title: <Link to={"/services"}>Services</Link>,
          },
          {
            title: "Create Service",
          },
        ]}
      />
      <div className="mt-3">
        <div className="flex justify-between">
          <h1 className="text-lg font-semibold mb-6">Create Service</h1>
          <div className="gap-2 flex">
            <Link to="/services">
              <Button>Cancel</Button>
            </Link>
            <Button type="primary" onClick={form.submit}>
              {ctcButtonTxt}
            </Button>
          </div>
        </div>
        <Form
          name="basic"
          form={form}
          initialValues={{ remember: true }}
          onFinish={handleSubmit}
          autoComplete="off"
          layout="vertical"
        >
          <Row>
            <Col span={10}>
              <Form.Item
                label="Enter a name for the service"
                name="name"
                rules={[{ required: true, message: "Please input name!" }]}
              >
                <Input autoComplete="off" size="large" placeholder="Name" />
              </Form.Item>
              <Form.Item
                label="A small description about the service"
                name="description"
              >
                <TextArea
                  autoComplete="off"
                  size="large"
                  placeholder="Description"
                  rows={4}
                />
              </Form.Item>
              {/* <Form.Item label="Image Url" name="imageUrl">
              <Input autoComplete="off" />
            </Form.Item> */}

              <Form.Item label="Gender" name="gender">
                <Select size="large">
                  <Select.Option value="MALE">Male</Select.Option>
                  <Select.Option value="FEMALE">Female</Select.Option>
                  <Select.Option value="UNISEX">Unisex</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item
                label="Duration"
                name="duration"
                rules={[{ required: true, message: "Please input duration!" }]}
              >
                <InputNumber
                  autoComplete="off"
                  className="w-full"
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>
          <Divider />
          <h2 className="text-base font-semibold mb-4">Store price</h2>
          <Row>
            <Col span={20}>
              <Form.List
                name="storeServices"
                rules={[
                  {
                    validator: async (_, storeServices) => {
                      if (!storeServices || storeServices.length < 1) {
                        return Promise.reject(
                          new Error("At least one store entry is required.")
                        );
                      }
                    },
                  },
                ]}
              >
                {(fields, { add, remove }) => {
                  // Get selected store IDs from current form values
                  const selectedStoreIds =
                    storeServices?.map((item: any) => item?.storeId) || [];

                  // Filter stores that are not selected
                  const getAvailableStores = (currentId?: string) => {
                    return stores.filter(
                      (store) =>
                        !selectedStoreIds.includes(store.id) ||
                        store.id === currentId
                    );
                  };

                  const canAddMore = stores.length > selectedStoreIds.length;

                  return (
                    <>
                      {fields.map(({ key, name, ...restField }, index) => {
                        const currentStoreId = storeServices?.[name]?.storeId;
                        return (
                          <div key={key} className="flex items-center gap-10">
                            <Form.Item
                              {...restField}
                              name={[name, "id"]}
                              hidden
                            ></Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, "storeId"]}
                              label="Store"
                              className="flex-1"
                              rules={[
                                {
                                  required: true,
                                  message: "StoreId is required",
                                },
                              ]}
                            >
                              <Select
                                options={getAvailableStores(currentStoreId).map(
                                  (store) => ({
                                    label: store.name,
                                    value: store.id,
                                  })
                                )}
                                placeholder="Store Name"
                                size="large"
                              />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, "price"]}
                              label="Price"
                              rules={[
                                {
                                  required: true,
                                  message: "Price is required",
                                },
                              ]}
                            >
                              <InputNumber
                                size="large"
                                addonBefore="₹"
                                autoComplete="off"
                                className="w-full"
                                placeholder="Price"
                                min={0}
                              />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, "memberPrice"]}
                              label="Member Price"
                              rules={[
                                {
                                  required: true,
                                  message: "Member price is required",
                                },
                              ]}
                            >
                              <InputNumber
                                size="large"
                                addonBefore="₹"
                                autoComplete="off"
                                className="w-full"
                                placeholder="Member Price"
                                min={0}
                              />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, "available"]}
                              label="Available"
                              rules={[
                                {
                                  required: true,
                                  message: "Missing availability",
                                },
                              ]}
                            >
                              <Switch />
                            </Form.Item>
                            {!(fields.length === 0) && (
                              <MinusCircleOutlined
                                onClick={() => {
                                  console.log(
                                    newFieldsIds,
                                    storeServices[name].id
                                  );
                                  if (
                                    !newFieldsIds.current.some(
                                      (id) => id === storeServices[name].idx
                                    )
                                  ) {
                                    removeStoreService(storeServices[name].id);
                                  }
                                  remove(name);
                                }}
                              />
                            )}
                          </div>
                        );
                      })}

                      <Form.Item>
                        <Button
                          type="dashed"
                          onClick={() => {
                            const newId = uuidv4();
                            newFieldsIds.current.push(newId);

                            add({
                              id: newId,
                              storeId: "",
                              price: undefined,
                              memberPrice: undefined,
                              available: true,
                            });
                          }}
                          block
                          icon={<PlusOutlined />}
                          disabled={!canAddMore}
                        >
                          Add field
                        </Button>
                      </Form.Item>
                    </>
                  );
                }}
              </Form.List>
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  );
};

export default CreateService;
